import { motion } from "framer-motion";
import { Atom, FlaskConical, Sigma, Loader2, Video, PenTool, Gauge, RotateCw, AlertTriangle, ArrowUpRight, ListOrdered, Clock } from "lucide-react";

export const GRADES = ["Grade 7", "Grade 8", "Grade 9", "Grade 10"];
const IMG = (id) => `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=1200&q=70`;
export const SUBJECTS = [
  { name: "Physics", icon: Atom, color: "#38BDF8", img: IMG("1782918843144-920ff238efc5") },
  { name: "Chemistry", icon: FlaskConical, color: "#A78BFA", img: IMG("1782926017910-26e8bcaffc6e") },
  { name: "Mathematics", icon: Sigma, color: "#F472B6", img: IMG("1770719269462-d3c1ed7e184f") },
];
export const subjectMeta = (name) => SUBJECTS.find((s) => s.name === name) || SUBJECTS[0];

const STATUS_META = {
  queued: { label: "In queue", color: "#818CF8", spin: false, step: 0 },
  processing: { label: "Starting", color: "#94A3B8", spin: true, step: 0 },
  transcribing: { label: "Transcribing audio", color: "#22D3EE", spin: true, step: 1 },
  generating: { label: "Writing notes", color: "#F59E0B", spin: true, step: 2 },
  ready: { label: "Ready", color: "#10B981", spin: false, step: 3 },
  failed: { label: "Failed", color: "#F43F5E", spin: false, step: -1 },
};
export const isPending = (m) => ["queued", "processing", "transcribing", "generating"].includes(m.status);
const fmtTime = (ts) => (ts ? new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "");
export { fmtTime };

export const StatusBadge = ({ status }) => {
  const s = STATUS_META[status] || STATUS_META.ready;
  return (
    <span data-testid="material-status-badge" className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full backdrop-blur-md"
      style={{ background: `${s.color}26`, color: s.color, border: `1px solid ${s.color}55` }}>
      {s.spin ? <Loader2 className="w-3 h-3 animate-spin" /> : <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }} />}{s.label}
    </span>
  );
};

const Steps = ({ status }) => {
  const step = (STATUS_META[status] || STATUS_META.ready).step;
  return (
    <div className="flex gap-1.5 mt-4" data-testid="material-progress-steps">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-1 flex-1 rounded-full bg-slate-800 overflow-hidden">
          <div className={`h-full rounded-full bg-emerald-400 ${step + 1 === i ? "animate-pulse" : ""}`} style={{ width: step >= i ? "100%" : step + 1 === i ? "50%" : "0%" }} />
        </div>
      ))}
    </div>
  );
};

export const Pill = ({ active, onClick, children, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm font-semibold border transition-[background-color,border-color,color] duration-200 ${active ? "bg-emerald-400 border-emerald-400 text-slate-950" : "bg-[#111726] border-[#1E293B] text-slate-400 hover:text-slate-100 hover:border-[#334155]"}`}>
    {children}
  </button>
);

export const SubjectTabs = ({ value, onChange }) => (
  <div className="grid grid-cols-3 gap-3">
    {SUBJECTS.map((s) => {
      const active = value === s.name;
      return (
        <button key={s.name} data-testid={`subject-pill-${s.name}`} onClick={() => onChange(s.name)}
          className={`relative overflow-hidden rounded-2xl border p-4 text-left transition-[border-color,transform] duration-200 hover:-translate-y-0.5 ${active ? "border-emerald-400/70" : "border-[#1E293B] hover:border-[#334155]"}`}>
          <img src={s.img} alt="" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${active ? "opacity-40" : "opacity-15"}`} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#090D16] via-[#090D16]/80 to-transparent" />
          <div className="relative flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${s.color}22`, border: `1px solid ${s.color}55` }}>
              <s.icon className="w-5 h-5" style={{ color: s.color }} />
            </div>
            <div>
              <div className={`font-display font-bold text-sm sm:text-base ${active ? "text-white" : "text-slate-300"}`}>{s.name}</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 hidden sm:block">{active ? "Selected" : "Open"}</div>
            </div>
          </div>
        </button>
      );
    })}
  </div>
);

export const StatTile = ({ icon: Icon, label, value, color }) => (
  <div className="bg-[#111726] border border-[#1E293B] rounded-2xl p-5" data-testid={`stat-${label.toLowerCase()}`}>
    <div className="flex items-center justify-between">
      <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <Icon className="w-4 h-4" style={{ color }} />
    </div>
    <div className="text-3xl font-extrabold font-display mt-3 tracking-tight">{value}</div>
  </div>
);

export const SectionTitle = ({ overline, title, right }) => (
  <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
    <div>
      {overline && <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-emerald-400 mb-1">{overline}</div>}
      <h2 className="text-xl sm:text-2xl font-bold font-display tracking-tight">{title}</h2>
    </div>
    {right}
  </div>
);

export function LessonCard({ m, onOpen, onRetry, onQueue, testid }) {
  const s = subjectMeta(m.subject);
  const failed = m.status === "failed";
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} data-testid={testid}
      className="group bg-[#111726] border border-[#1E293B] rounded-2xl overflow-hidden hover:border-emerald-500/50 hover:shadow-lg hover:shadow-emerald-500/5 transition-[border-color,box-shadow] duration-200">
      <button onClick={onOpen} className="block w-full text-left" data-testid={`${testid}-open`}>
        <div className="relative h-28 overflow-hidden">
          <img src={s.img} alt="" className="w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-500" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#111726] to-transparent" />
          <div className="absolute top-3 left-3"><StatusBadge status={m.status || "ready"} /></div>
          <ArrowUpRight className="absolute top-3 right-3 w-4 h-4 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
        <div className="px-5 pb-5 -mt-2 relative">
          <div className="font-display font-bold text-slate-100 leading-snug line-clamp-2">{m.title}</div>
          <div className="flex items-center gap-3 text-xs text-slate-500 mt-2">
            <span className="inline-flex items-center gap-1" style={{ color: s.color }}><s.icon className="w-3 h-3" /> {m.subject}</span>
            {m.video_url && <span className="inline-flex items-center gap-1"><Video className="w-3 h-3" /> Video</span>}
            {m.miro_url && <span className="inline-flex items-center gap-1"><PenTool className="w-3 h-3" /> Board</span>}
            {m.tokens_used ? <span className="ml-auto inline-flex items-center gap-1 text-emerald-400/80 font-mono" data-testid={`${testid}-tokens`}><Gauge className="w-3 h-3" />{(m.tokens_used / 1000).toFixed(1)}k</span> : null}
          </div>
          {m.status === "queued" && (
            <div className="mt-3 text-xs text-indigo-300 flex items-center gap-1.5" data-testid={`${testid}-queue-info`}>
              <Clock className="w-3.5 h-3.5" /> Next try ~{fmtTime(m.next_try_at)} · ready by {fmtTime(m.deadline_at)}
            </div>
          )}
          {isPending(m) && <Steps status={m.status} />}
        </div>
      </button>
      {failed && (
        <div className="mx-5 mb-5 p-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-xs text-rose-300 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <div className="flex-1"><div data-testid={`${testid}-error`}>{m.error || "Processing failed."}</div>
            <div className="flex gap-4">
              {onRetry && <button data-testid={`${testid}-retry`} onClick={onRetry} className="mt-2 inline-flex items-center gap-1.5 font-semibold text-rose-200 hover:text-white transition-colors"><RotateCw className="w-3.5 h-3.5" /> Retry now</button>}
              {onQueue && <button data-testid={`${testid}-queue`} onClick={onQueue} className="mt-2 inline-flex items-center gap-1.5 font-semibold text-indigo-300 hover:text-white transition-colors"><ListOrdered className="w-3.5 h-3.5" /> Add to queue</button>}
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
